import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowRight, Compass } from "lucide-react";
import { useNoIndex } from "@/lib/useNoIndex";

const LOGO = "/assets/logo.png";

export default function NotFound() {
  useNoIndex();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain relative flex flex-col">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-[#D4AF37]/8 blur-[130px] rounded-full pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="notfound-home-link">
            <img src={LOGO} alt="Social Media Combo logo" className="w-8 h-8 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Social Media Combo</span>
              <span className="font-serif-display text-base tracking-[0.25em] font-semibold">
                THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
              </span>
            </div>
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-xl"
          data-testid="notfound-page"
        >
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 mb-8">
            <Compass className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Page not found</span>
          </div>

          <h1 className="font-serif-display text-7xl sm:text-8xl font-bold gold-shimmer leading-none">404</h1>

          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold mt-6">
            This page has moved on
          </h2>
          <p className="mt-4 text-lg text-zinc-400 leading-relaxed">
            The link you followed is no longer here — but your faceless empire still is. Head back to
            grab all 11 ebooks + 3 free bonuses and start today.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              data-testid="notfound-cta-button"
              className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-sm"
            >
              <Home className="w-4 h-4" /> Take Me to the Store
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors"
            >
              View the bundle <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-900 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-zinc-600">
          <span className="font-serif-display tracking-widest">SOCIAL MEDIA COMBO.</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
            <span>© {new Date().getFullYear()} socialmediacombo.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
